import Modal from '@/src/Common/components/Modal';
import useMediaTags from './hooks/useMediaTags';
import { Dialog } from '@headlessui/react';
import { TagIcon } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

// Mirrors cloud/modules/mediaTags/service normalizeTag so chips shown here
// match what the server will store.
function normalizeTag(tag: string): string {
    return tag.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Bulk tag apply/remove over the media manager selection, keyed by
 * shareIdentifier (the shape the selection already has on hand). Applied
 * tags are recorded with source 'admin', distinct from booth setTag actions.
 */
export default function BulkTagModal({
    shareIdentifiers,
    selectedPhotos,
    allPhotos,
    onDismiss,
    onComplete,
}: {
    shareIdentifiers: string[];
    /** Selection rows — drives the remove-mode suggestions. */
    selectedPhotos: MediaInstance[];
    /** Every loaded row — drives the add-mode suggestions. */
    allPhotos: MediaInstance[];
    onDismiss: () => void;
    onComplete: () => void;
}) {
    const cancelButtonRef = useRef(null);
    const { addTagsByShareIdentifiers, removeTagsByShareIdentifiers } =
        useMediaTags();
    const [mode, setMode] = useState<'add' | 'remove'>('add');
    const [tags, setTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Add mode suggests every tag in the activation; remove mode only tags
    // actually present on the selection.
    const suggestedTags = useMemo(() => {
        const source = mode === 'add' ? allPhotos : selectedPhotos;
        const counts = new Map<string, number>();

        for (const photo of source) {
            for (const tag of photo.tags ?? []) {
                counts.set(tag, (counts.get(tag) ?? 0) + 1);
            }
        }

        return Array.from(counts.entries())
            .filter(([tag]) => !tags.includes(tag))
            .sort((a, b) => b[1] - a[1]);
    }, [mode, allPhotos, selectedPhotos, tags]);

    const addTag = (rawTag: string) => {
        const tag = normalizeTag(rawTag);

        if (!tag || tags.includes(tag)) {
            return;
        }

        setTags([...tags, tag]);
        setTagInput('');
    };

    const apply = async () => {
        if (tags.length === 0) {
            return;
        }

        setIsSaving(true);

        try {
            if (mode === 'add') {
                const response = await addTagsByShareIdentifiers(
                    shareIdentifiers,
                    tags,
                );
                toast.success(
                    `Applied ${tags.length} ${
                        tags.length === 1 ? 'tag' : 'tags'
                    } (${response.data.added} added)`,
                );
            } else {
                const response = await removeTagsByShareIdentifiers(
                    shareIdentifiers,
                    tags,
                );
                toast.success(`Removed ${response.data.removed} tags`);
            }

            onComplete();
        } catch (e) {
            console.error(e);
            toast.error(
                mode === 'add'
                    ? 'Error applying tags'
                    : 'Error removing tags',
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal cancelButtonRef={cancelButtonRef} onDismiss={onDismiss}>
            <div>
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[99999px] bg-black sm:mx-0 sm:h-10 sm:w-10">
                            <TagIcon
                                className="h-6 w-6 text-white"
                                aria-hidden="true"
                            />
                        </div>
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                            <Dialog.Title
                                as="h3"
                                className="text-base font-semibold leading-6 text-gray-900"
                            >
                                Tag {shareIdentifiers.length}{' '}
                                {shareIdentifiers.length === 1
                                    ? 'photo'
                                    : 'photos'}
                            </Dialog.Title>
                            <div className="mt-3 space-y-4">
                                <div className="inline-flex rounded-md border border-gray-200 p-0.5">
                                    <button
                                        type="button"
                                        className={`px-3 py-1 text-sm rounded ${
                                            mode === 'add'
                                                ? 'bg-black text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                        onClick={() => setMode('add')}
                                    >
                                        Apply tags
                                    </button>
                                    <button
                                        type="button"
                                        className={`px-3 py-1 text-sm rounded ${
                                            mode === 'remove'
                                                ? 'bg-black text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                        onClick={() => setMode('remove')}
                                    >
                                        Remove tags
                                    </button>
                                </div>

                                <div className="flex flex-wrap items-center gap-1">
                                    {tags.map((tag) => (
                                        <span
                                            key={tag}
                                            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                                        >
                                            {tag}
                                            <button
                                                type="button"
                                                className="hover:text-red-500"
                                                onClick={() =>
                                                    setTags(
                                                        tags.filter(
                                                            (t) => t !== tag,
                                                        ),
                                                    )
                                                }
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                    {tags.length === 0 && (
                                        <span className="text-xs text-gray-500">
                                            {mode === 'add'
                                                ? 'Choose tags to apply to the selection'
                                                : 'Choose tags to remove from the selection'}
                                        </span>
                                    )}
                                </div>

                                <input
                                    type="text"
                                    className="block w-full p-2 rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                    placeholder="Type a tag and press Enter"
                                    value={tagInput}
                                    onChange={(e) =>
                                        setTagInput(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            addTag(tagInput);
                                        }
                                    }}
                                />

                                {suggestedTags.length > 0 && (
                                    <div className="flex flex-wrap items-center gap-1">
                                        <span className="text-xs text-gray-500">
                                            {mode === 'add'
                                                ? 'In use:'
                                                : 'On selection:'}
                                        </span>
                                        {suggestedTags.map(([tag, count]) => (
                                            <button
                                                key={tag}
                                                type="button"
                                                className="inline-flex items-center rounded-full border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100"
                                                onClick={() => addTag(tag)}
                                            >
                                                + {tag}
                                                <span className="ml-1 text-gray-400">
                                                    {count}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                    type="button"
                    className="mt-3 inline-flex w-full ml-4 justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => onDismiss()}
                    ref={cancelButtonRef}
                >
                    Cancel
                </button>
                <br />
                <button
                    onClick={apply}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-gray-800 hover:bg-black-50 sm:mt-0 sm:w-auto disabled:opacity-50"
                    disabled={isSaving || tags.length === 0}
                >
                    {isSaving
                        ? 'Saving...'
                        : mode === 'add'
                          ? 'Apply Tags'
                          : 'Remove Tags'}
                </button>
            </div>
        </Modal>
    );
}
